// Three small charts, built in plain HTML. Specs from the dataviz method:
// thin bars (<=24px), 4px rounded data-end and square baseline, recessive hairlines,
// text in text tokens, per-mark tooltip on hover and focus, values also visible without it.
import { useState, type ReactNode } from 'react';

import { fmtDay, fmtDuration } from '../../lib/format';

interface Tip {
  x: number;
  y: number;
  body: ReactNode;
}

const useTip = () => {
  const [tip, setTip] = useState<Tip | null>(null);
  const bind = (body: ReactNode) => ({
    tabIndex: 0,
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
      const host = (e.currentTarget.closest('.chart') as HTMLElement).getBoundingClientRect();
      setTip({ x: e.clientX - host.left, y: e.clientY - host.top, body });
    },
    onFocus: (e: React.FocusEvent<HTMLElement>) => {
      const host = (e.currentTarget.closest('.chart') as HTMLElement).getBoundingClientRect();
      const r = e.currentTarget.getBoundingClientRect();
      setTip({ x: r.left - host.left + r.width / 2, y: r.top - host.top, body });
    },
    onPointerLeave: () => setTip(null),
    onBlur: () => setTip(null),
  });
  const layer = tip ? (
    <div className="tip" style={{ left: tip.x, top: tip.y }} role="tooltip">
      {tip.body}
    </div>
  ) : null;
  return { bind, layer };
};

// Where the time went: one series, emphasis form (the top section in the accent, the rest in gray)
export const SectionBars = ({ sections }: { sections: Array<{ key: string; label: string; ms: number; views: number }> }) => {
  const { bind, layer } = useTip();
  const total = sections.reduce((s, x) => s + x.ms, 0);
  const max = Math.max(1, ...sections.map((s) => s.ms));
  const top = sections.reduce<string | null>((k, s) => (k === null || s.ms > (sections.find((x) => x.key === k)?.ms ?? 0) ? s.key : k), null);
  return (
    <div className="chart hbars">
      {sections.map((s) => {
        const share = total ? Math.round((s.ms / total) * 100) : 0;
        return (
          <div
            className="hbar"
            key={s.key}
            data-top={s.key === top || undefined}
            aria-label={`${s.label}: ${fmtDuration(s.ms)}, ${share}% do tempo`}
            {...bind(
              <>
                <strong>{fmtDuration(s.ms)}</strong>
                <span>{s.label}</span>
                <span>
                  {share}% do tempo · em {s.views} {s.views === 1 ? 'abertura' : 'aberturas'}
                </span>
              </>
            )}
          >
            <span className="hbar-l">{s.label}</span>
            <span className="hbar-t">
              <i style={{ width: `${Math.max(1.5, (s.ms / max) * 100)}%` }} />
            </span>
            <span className="hbar-v num">
              {fmtDuration(s.ms)} <em>{share}%</em>
            </span>
          </div>
        );
      })}
      {layer}
    </div>
  );
};

// How far they got: share of openings that passed each depth
export const ReachBars = ({ reach }: { reach: { total: number; p25: number; p50: number; p75: number; p100: number } }) => {
  const { bind, layer } = useTip();
  const rows = [
    { k: 'p25', label: 'Passou de 25%', n: reach.p25 },
    { k: 'p50', label: 'Chegou à metade', n: reach.p50 },
    { k: 'p75', label: 'Passou de 75%', n: reach.p75 },
    { k: 'p100', label: 'Chegou ao fim', n: reach.p100 },
  ];
  return (
    <div className="chart hbars">
      {rows.map((r) => {
        const pct = reach.total ? Math.round((r.n / reach.total) * 100) : 0;
        return (
          <div
            className="hbar"
            data-top
            key={r.k}
            aria-label={`${r.label}: ${r.n} de ${reach.total} aberturas`}
            {...bind(
              <>
                <strong>{pct}%</strong>
                <span>{r.label}</span>
                <span>
                  {r.n} de {reach.total} {reach.total === 1 ? 'abertura' : 'aberturas'}
                </span>
              </>
            )}
          >
            <span className="hbar-l">{r.label}</span>
            <span className="hbar-t">
              <i style={{ width: `${Math.max(pct ? 1.5 : 0, pct)}%` }} />
            </span>
            <span className="hbar-v num">
              {pct}% <em>
                {r.n}/{reach.total}
              </em>
            </span>
          </div>
        );
      })}
      {layer}
    </div>
  );
};

// Openings per day, last 14 days: one series; label only the peak and today
export const OpensColumns = ({ days }: { days: Array<{ day: string; opens: number }> }) => {
  const { bind, layer } = useTip();
  const [table, setTable] = useState(false);
  const max = Math.max(1, ...days.map((d) => d.opens));
  const peak = days.reduce((p, d, i) => (d.opens > days[p].opens ? i : p), 0);
  const ticks = Math.max(1, Math.ceil(max / 2));
  const at = (d: string) => `${d}T12:00:00Z`;

  return (
    <div>
      <div className="chart-tools">
        <button type="button" className="btn-link small" onClick={() => setTable(!table)}>
          {table ? 'Ver gráfico' : 'Ver tabela'}
        </button>
      </div>
      {table ? (
        <table className="table compact">
          <thead>
            <tr>
              <th>Dia</th>
              <th className="r">Aberturas</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.day}>
                <td>{fmtDay(at(d.day))}</td>
                <td className="r num">{d.opens}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="chart cols" role="img" aria-label={`Aberturas por dia nos últimos 14 dias; pico de ${days[peak].opens}`}>
          <div className="cols-grid" aria-hidden="true">
            {[ticks * 2, ticks, 0].map((t) => (
              <span key={t} style={{ bottom: `${(t / (ticks * 2)) * 100}%` }}>
                <em className="num">{t}</em>
              </span>
            ))}
          </div>
          <div className="cols-plot">
            {days.map((d, i) => {
              const show = d.opens > 0 && (i === peak || i === days.length - 1);
              return (
                <div
                  className="col"
                  key={d.day}
                  aria-label={`${fmtDay(at(d.day))}: ${d.opens} aberturas`}
                  {...bind(
                    <>
                      <strong>{d.opens}</strong>
                      <span>{d.opens === 1 ? 'abertura' : 'aberturas'}</span>
                      <span>{fmtDay(at(d.day))}</span>
                    </>
                  )}
                >
                  <i style={{ height: `${(d.opens / (ticks * 2)) * 100}%` }} data-zero={d.opens === 0 || undefined}>
                    {show && <b className="num">{d.opens}</b>}
                  </i>
                </div>
              );
            })}
          </div>
          <div className="cols-x" aria-hidden="true">
            <span>{fmtDay(at(days[0].day))}</span>
            <span>{fmtDay(at(days[Math.floor(days.length / 2)].day))}</span>
            <span>hoje</span>
          </div>
          {layer}
        </div>
      )}
    </div>
  );
};
